import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import posthog from 'posthog-js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data }) => {
      setUser(data?.session?.user ?? null);
      if (data?.session?.user) {
        posthog.identify(data.session.user.id, {
          email: data.session.user.email,
          name: data.session.user.user_metadata?.full_name
        });
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          name: session.user.user_metadata?.full_name
        });
      } else if (_event === 'SIGNED_OUT') {
        posthog.reset();
      }
    });

    // Handle Safari bfcache - re-check session when page is restored
    const handlePageShow = async (event) => {
      if (event.persisted) {
        console.log("Restored from bfcache, refreshing session...");
        const { data } = await supabase.auth.getSession();
        setUser(data?.session?.user ?? null);
        if (data?.session?.user) {
          posthog.identify(data.session.user.id, {
            email: data.session.user.email,
            name: data.session.user.user_metadata?.full_name
          });
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const resetPassword = (email, options = {}) => {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
      captchaToken: options.captchaToken,
    });
  };

  const updatePassword = (newPassword) => {
    return supabase.auth.updateUser({ password: newPassword });
  };

  const signOut = async () => {
    setUser(null); // Optimistic update to clear UI immediately
    setLoading(true);
    try {
      await supabase.auth.signOut();
      posthog.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, resetPassword, updatePassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
